import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

const LOAD_AHEAD = 600;

// Observe the real scroll container on web; native uses ScrollView metrics.
// Both start the next page before the user reaches the end of the current one.
export default function useFilesAutoLoad({ enabled, loadMore }) {
  const scrollRef = useRef(null);
  const footerRef = useRef(null);
  const metrics = useRef({ height: 0, content: 0, offset: 0 });

  const checkPosition = useCallback(() => {
    if (!enabled) return;
    const { height, content, offset } = metrics.current;
    if (height > 0 && content > 0 && content - height - offset <= LOAD_AHEAD) {
      void loadMore();
    }
  }, [enabled, loadMore]);

  const onScroll = useCallback(({ nativeEvent }) => {
    const { layoutMeasurement, contentSize, contentOffset } = nativeEvent;
    metrics.current = {
      height: layoutMeasurement.height,
      content: contentSize.height,
      offset: Math.max(0, contentOffset.y),
    };
    checkPosition();
  }, [checkPosition]);

  const onLayout = useCallback(({ nativeEvent }) => {
    metrics.current.height = nativeEvent.layout.height;
    checkPosition();
  }, [checkPosition]);

  const onContentSizeChange = useCallback((_, height) => {
    metrics.current.content = height;
    // A filter can replace a long list while it was scrolled near the end.
    metrics.current.offset = Math.min(metrics.current.offset, Math.max(0, height - metrics.current.height));
    checkPosition();
  }, [checkPosition]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled || typeof IntersectionObserver === 'undefined') return;
    const footer = footerRef.current;
    if (!footer) return;
    let active = true;
    const observer = new IntersectionObserver(entries => {
      if (active && entries.some(entry => entry.isIntersecting)) void loadMore();
    }, { root: scrollRef.current?.getScrollableNode?.() || null, rootMargin: `${LOAD_AHEAD}px 0px` });
    observer.observe(footer);
    return () => { active = false; observer.disconnect(); };
  }, [enabled, loadMore]);

  return { scrollRef, footerRef, onScroll, onLayout, onContentSizeChange };
}
